import React, { useEffect, useRef, useState } from "react";
import { Accordion, AccordionItem, Button } from "@heroui/react";
import AppButton from "../../common/AppButton";
import NestedAccordionItemContent from "./NestedSectionItemContent";
import classNames from 'classnames';
import { RotateCcw } from "lucide-react";
import EditHeadingModal from "../heading/EditHeadingModal";
import IndicatorSection from "./IndicatorSection";

const NestedSection = React.forwardRef(
  (
    {
      section,
      sectionIndex,
      targetNested,
      isActive,
      selectedNestedSectionMap,
      selectedSectionMap,
      selectedSectionKey,
      setSelectedSectionKey,
      currentTime,
      isPlaying = false,
      setCurrentTime,
      sectionTime,
      setNewTimeSetted,
      setCorrectedParagraphs,
      correctedParagraphs,
      sessionId,
      setIsAddSectionModalOpen,
      setSelectedSpeakers,
      allSections,
      speakers,
      openAllNestedToken,
      expandAll,
      promptMode = "soft",
      onOpenSpeakersModal,
      sessionStart,
    },
    ref
  ) => {  const nestedRefs = useRef([]);
  // User-managed open nested keys
  const [selectedNestedKey, setSelectedNestedKey] = useState([]);
  // Time-sync auto-open key (single)
  const [autoNestedKey, setAutoNestedKey] = useState(null);
  const initializedNestedOpenRef = useRef(false);

  // Setup refs
  if (nestedRefs.current.length !== section.children.length) {
    nestedRefs.current = Array(section.children.filter(section => section.type == "paragraph").length)
      .fill()
      .map((_, i) => nestedRefs.current[i] || React.createRef());
  }

  // Auto open nested when active: set a single auto key (do not accumulate)
  useEffect(() => {
    if (isActive && targetNested !== null) {
      const nestedKeyForAccordion = `section-${sectionIndex}-nested-${targetNested}`;
      setAutoNestedKey(nestedKeyForAccordion);
    }
  }, [isActive, targetNested, sectionIndex]);

  function countParagraphsUpToIndex(children, i) {
    if (!Array.isArray(children) || i < 0 || i >= children.length) return 0;
  
    let count = 0;
    for (let j = 0; j <= i; j++) {
      if (children[j].type === "paragraph") {
        count++;
      }
    }
    return count;
  }

  // No internal scrolling; parent orchestrates scrolling for stability

  // Absolute clock time = session's recorded start + the paragraph's
  // offset (`seconds` since the recording began). The session start is
  // the ISO timestamp the user picked when creating the session, e.g.
  // "2024-01-15T16:01:23Z". A paragraph at offset 17s renders as
  // "16:01:40" instead of "0:17". Falls back to the previous relative
  // format if sessionStart isn't available (e.g. on older sessions
  // that don't have it populated).
  const formatTime = (seconds) => {
    const safeSeconds = Number(seconds) || 0;
    if (sessionStart) {
      const startDate = new Date(sessionStart);
      if (!Number.isNaN(startDate.getTime())) {
        const wallClock = new Date(startDate.getTime() + safeSeconds * 1000);
        const pad = (n) => String(n).padStart(2, "0");
        return `${pad(wallClock.getUTCHours())}:${pad(wallClock.getUTCMinutes())}:${pad(wallClock.getUTCSeconds())}`;
      }
    }
    const mins = Math.floor(safeSeconds / 60);
    const secs = Math.floor(safeSeconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const getSpeakerDisplayLabel = (speakerName, speakerNumber) => {
    const normalizedName = String(speakerName ?? "").trim();
    const hasSpeakerName =
      normalizedName.length > 0 && normalizedName.toLowerCase() !== "no";
    return hasSpeakerName
      ? normalizedName
      : `Speaker ${speakerNumber ?? ""}`.trim();
  };

  const allParagraphs = React.useMemo(() => {
    if (!Array.isArray(allSections)) return [];
    const list = [];
    allSections.forEach((sectionItem) => {
      if (!Array.isArray(sectionItem?.children)) return;
      sectionItem.children.forEach((child) => {
        if (child?.type === "paragraph") list.push(child);
      });
    });
    return list;
  }, [allSections]);

  const paragraphIndexById = React.useMemo(() => {
    const map = new Map();
    for (let i = 0; i < allParagraphs.length; i += 1) {
      const id = allParagraphs[i]?.id;
      if (id != null) map.set(id, i);
    }
    return map;
  }, [allParagraphs]);

  const paragraphWindowById = React.useMemo(() => {
    const map = new Map();
    for (let i = 0; i < allParagraphs.length; i += 1) {
      const current = allParagraphs[i];
      const id = current?.id;
      const start = Number(current?.start);
      if (id == null || !Number.isFinite(start)) continue;
      const nextStartRaw = Number(allParagraphs[i + 1]?.start);
      const end = Number.isFinite(nextStartRaw) ? nextStartRaw : Infinity;
      map.set(id, { start, end });
    }
    return map;
  }, [allParagraphs]);

  const getAdjacentParagraphs = (currentParagraph, currentIndex) => {
    let previousParagraph = null;
    let nextParagraph = null;

    if (!currentParagraph || !currentParagraph.id) {
      return { previousParagraph, nextParagraph };
    }

    // Find current paragraph index in the flattened list
    const currentGlobalIndex = paragraphIndexById.get(currentParagraph.id);
    
    if (typeof currentGlobalIndex === "number" && currentGlobalIndex > 0) {
      previousParagraph = allParagraphs[currentGlobalIndex - 1];
    }
    
    if (typeof currentGlobalIndex === "number" && currentGlobalIndex < allParagraphs.length - 1) {
      nextParagraph = allParagraphs[currentGlobalIndex + 1];
    }

    return { previousParagraph, nextParagraph };
  };

  const itemClasses = {
    base: "py-2 pb-0 ",
    trigger: "py-2 px-0",
    title: "mb-0"
  };

  const itemClasses2 = {
    base: "py-1 pb-1 px-0 rounded-xl",
    trigger: "py-2 pb-0 px-0",
    title: "mb-0",
    content:"px-0"
  };

  // Build the allowed nested keys (only paragraph entries)
  const paragraphKeysForSection = React.useMemo(() => {
    const keys = [];
    let paragraphCounter = 0;
    for (let i = 0; i < section.children.length; i++) {
      if (section.children[i].type === "paragraph") {
        paragraphCounter += 1;
        keys.push(`section-${sectionIndex}-nested-${paragraphCounter}`);
      }
    }
    return keys;
  }, [section, sectionIndex]);
  const paragraphKeysSignature = React.useMemo(
    () => paragraphKeysForSection.join("|"),
    [paragraphKeysForSection]
  );
  const paragraphKeySet = React.useMemo(() => new Set(paragraphKeysForSection), [paragraphKeysForSection]);

  const syncSelectedNestedKeys = React.useCallback((updater) => {
    setSelectedNestedKey((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (
        Array.isArray(prev) &&
        Array.isArray(next) &&
        prev.length === next.length &&
        prev.every((value, index) => value === next[index])
      ) {
        return prev;
      }
      return next;
    });
  }, [setSelectedNestedKey]);

  // Open all nested items on first mount
  useEffect(() => {
    if (!initializedNestedOpenRef.current && paragraphKeysForSection && paragraphKeysForSection.length > 0) {
            syncSelectedNestedKeys(paragraphKeysForSection);
            initializedNestedOpenRef.current = true;
          }
        }, [paragraphKeysSignature, syncSelectedNestedKeys]);
  
  // Open/close all nested speaker items when requested
  useEffect(() => {
    if (!openAllNestedToken) return;
    syncSelectedNestedKeys(expandAll ? paragraphKeysForSection : []);
  }, [openAllNestedToken, expandAll, paragraphKeysSignature, syncSelectedNestedKeys, paragraphKeysForSection]);

  const renderNestedSection = (nestedSectionData, i) => {
    const indexParagraphRef = countParagraphsUpToIndex(section.children, i);
    const nestedKey = `section-${sectionIndex}-nested-${indexParagraphRef}`;
    const contentVersion = `${nestedSectionData?.original_content?.length || 0}-${nestedSectionData?.corrected_content?.length || 0}`;
  
    const isSelected = selectedNestedSectionMap === nestedKey;
    
    // Check if this paragraph is currently playing based on currentTime
    let isCurrentlyPlaying = false;
    if (nestedSectionData.type === "paragraph") {
      const window = paragraphWindowById.get(nestedSectionData.id);
      if (window) {
        isCurrentlyPlaying = currentTime >= window.start && currentTime < window.end;
      }
    }
  
    if (nestedSectionData.type === "paragraph") {
      return (
        <AccordionItem 
        
          key={nestedKey} 
          title={<p className={classNames(
                  "px-4 mt-0 py-0 mb-0 transition-all duration-150 flex-1 mr-0 relative z-10",
                  {
                    "font-medium": isCurrentlyPlaying,
                    "": !isCurrentlyPlaying
                  }
                )}>
                  {getSpeakerDisplayLabel(
                    nestedSectionData.speaker_name,
                    nestedSectionData.speaker_number
                  )}

                </p>} 
          onPress={() => setNewTimeSetted(nestedSectionData.start)}      
          indicator={
            <div className={classNames(
              "flex p-0 transition-all duration-150 pe-4 pl-0 relative z-10",
              {}
            )}>
              {/* {isSelected &&
                <AppButton
                className="p-2 me-2 z-10"
              >
                <RotateCcw className="w-4 h-4 text-gray-700" />
              </AppButton>
              } */}
              

              <button 
                onClick={() => setNewTimeSetted(nestedSectionData.start)}
                className={classNames(
                  "py-0 text-base hover:underline cursor-pointer",
                  {
                    "text-onSecondaryContainer font-medium": isCurrentlyPlaying,
                    "": !isCurrentlyPlaying
                  }
                )}
              >
                {formatTime(nestedSectionData.start)}
              </button>

            </div>
          
          }

        
          disableIndicatorAnimation={true}
          classNames={{
            trigger: classNames(
              "transition-all duration-150 relative",
              {
                "text-onSecondaryContainer before:content-[''] before:absolute before:inset-x-0 before:top-1 before:bottom-1 before:bg-secondaryContainer before:rounded-md":
                  isCurrentlyPlaying
              }
            )
          }}
          className={classNames(
            "transition-colors duration-300 ease-in-out shadow-0 mb-0",
            {
              "bg-surface": isSelected,
              "bg-surface": !isSelected
            }
          )}
        >
          
          <NestedAccordionItemContent 
            key={`${nestedKey}-content-${contentVersion}`}
            indexParagraphRef={indexParagraphRef} 
            nestedKey={nestedKey} 
            nestedRefs={nestedRefs} 
            nestedSectionData={nestedSectionData} 
            correctedParagraphs={correctedParagraphs} 
            setCorrectedParagraphs={setCorrectedParagraphs} 
            sessionId={sessionId} 
            setSelectedSpeakers={setSelectedSpeakers}
            {...getAdjacentParagraphs(nestedSectionData, i)}
            speakers={speakers}
            currentTime={isCurrentlyPlaying ? currentTime : null}
            isPlaying={isPlaying && isCurrentlyPlaying}
            onRequestSeek={setNewTimeSetted}
            promptMode={promptMode}
            onOpenSpeakersModal={onOpenSpeakersModal}
          />
        </AccordionItem>
      );
    } else {
      return (
        <AccordionItem 
          key={`section-${sectionIndex}-nested-${i}-h3`} 
          title={<p className="px-4 mt-0 py-0 mb-0 ">{nestedSectionData.content}</p>} 
          indicator={
              <IndicatorSection
                formatTime={formatTime}
                section={nestedSectionData}
                sectionTime={nestedSectionData.start}
                setNewTimeSetted={setNewTimeSetted}
                sessionId={sessionId}
                setIsAddSectionModalOpen={setIsAddSectionModalOpen}
              />
              
            
          }
          disableIndicatorAnimation={true}
        >
          
        </AccordionItem>
      );
    }
  };



  // Flat mode: if no section title, render only nested items without a section header
  if (!section?.heading2?.content) {
    return (
      <div ref={ref} className="px-0 py-0 bg-surface rounded-xl">
        <Accordion
          selectedKeys={[...new Set([...(selectedNestedKey || []), ...(autoNestedKey ? [autoNestedKey] : [])]) ]}
          selectionMode="multiple"
          itemClasses={itemClasses2}
          className="p-0"
          onSelectionChange={(keys) => {
            const next = Array.from(keys || []).filter((k) => paragraphKeySet.has(k));
            setSelectedNestedKey(next);
          }}
          dividerProps={{
            className: "h-[1px] px-0 m-0 bg-gray-400"
          }}
        >
          {section.children.map((nestedSectionData, i) => renderNestedSection(nestedSectionData, i))}
        </Accordion>
      </div>
    );
  }

  return (
    <div ref={ref} className="px-0 py-0 bg-surface rounded-xl">
      <Accordion disableAnimation={false}
        selectedKeys={selectedSectionKey?.includes(`section-${sectionIndex}`) ? [`section-${sectionIndex}`] : []}
        itemClasses={itemClasses}
        selectionMode="multiple"
        className="text-onSurface rounded-xl px-0"
        onSelectionChange={(keys) => {
          const arr = Array.from(keys || []);
          const thisKey = `section-${sectionIndex}`;
          const isNowSelected = arr.includes(thisKey);
          setSelectedSectionKey((prev) => {
            const next = new Set(prev || []);
            if (isNowSelected) next.add(thisKey); else next.delete(thisKey);
            return Array.from(next);
          });
        }}

      >
        <AccordionItem 
          key={`section-${sectionIndex}`} 
          onPress={() => setNewTimeSetted(sectionTime)}
          title={<p className=" px-4 m-0">{section.heading2.content}</p>} 
          className="bg-surface text-onSurface py-0 m-0 rounded-xl shadow-0 p-0"
          indicator={
              <IndicatorSection
                formatTime={formatTime}
                section={section.heading2}
                sectionTime={sectionTime}
                setNewTimeSetted={setNewTimeSetted}
                sessionId={sessionId}
                setIsAddSectionModalOpen={setIsAddSectionModalOpen}
              />
              
            
          }
          disableIndicatorAnimation={true}
        >
          <hr className="h-[1px] bg-gray-400 mb-0 mt-0"></hr>

          <Accordion
            selectedKeys={[...new Set([...(selectedNestedKey || []), ...(autoNestedKey ? [autoNestedKey] : [])]) ]}
            selectionMode="multiple"
            itemClasses={itemClasses2}
            className="p-0"
            onSelectionChange={(keys) => {
              // Filter to only paragraph keys
              const next = Array.from(keys || []).filter((k) => paragraphKeySet.has(k));
              setSelectedNestedKey(next);
            }}
            dividerProps={{
              className: "h-[1px] px-0 m-0 bg-gray-400"
            }}
            >
            {section.children.map((nestedSectionData, i) => 
                
                renderNestedSection(nestedSectionData, i)
                
              )}
          </Accordion>
        </AccordionItem>
      </Accordion>
    </div>
  );
});

export default React.memo(NestedSection);
